import { createContext, useContext, useState, useCallback } from 'react'
import CONFIG from '../config'

const SettingsContext = createContext(null)

const defaultSettings = {
  theme: CONFIG.DEFAULT_THEME,
  fontSize: CONFIG.DEFAULT_FONT_SIZE,
  timezone: CONFIG.DEFAULT_TIMEZONE,
  timeFormat: CONFIG.DEFAULT_TIME_FORMAT,
  updateInterval: CONFIG.DEFAULT_UPDATE_INTERVAL,
  scanLines: CONFIG.ENABLE_SCAN_LINES,
  glowEffect: CONFIG.ENABLE_GLOW_EFFECT,
  autoScroll: true,
  liveUpdates: true
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('logViewerSettings')
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) }
      } catch (error) {
        console.error('Failed to load settings:', error)
      }
    }
    return defaultSettings
  })

  const saveSettings = useCallback((newSettings) => {
    try {
      localStorage.setItem('logViewerSettings', JSON.stringify(newSettings))
    } catch (error) {
      console.error('Failed to save settings:', error)
    }
  }, [])

  const setSetting = useCallback((key, value) => {
    setSettings(prev => {
      const updated = { ...prev, [key]: value }
      saveSettings(updated)
      return updated
    })
  }, [saveSettings])

  const resetToDefaults = useCallback(() => {
    setSettings(defaultSettings)
    saveSettings(defaultSettings)
  }, [saveSettings])

  const value = {
    settings,
    setSetting,
    setSettings: (newSettings) => {
      setSettings(newSettings)
      saveSettings(newSettings)
    },
    resetToDefaults
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}

export function useSettings() {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}
